import { View, Text, Pressable } from 'react-native'
import React, { useMemo } from 'react'
import { StyleSheet } from 'react-native-unistyles'
import { Table } from '@/components/General/Table'
import Avatar from '@/components/People/Avatar'
import { DateTime } from 'luxon'
import { Query } from '@/utils/react-query/Queries'
import usePlatform from '@/utils/hooks/usePlatform'
import { useFilter } from '@/data/store/useFilterStore'
import PeopleAdvancedFilter from '@/components/Filters/PeopleAdvancedFilter'
import { openURL } from '@/utils/linking'

const DAYS_OPTIONS = [
    { label: '7 Days', value: 7 },
    { label: '30 Days', value: 30 },
    { label: '90 Days', value: 90 },
    { label: '180 Days', value: 180 },
    { label: '360 Days', value: 360 },
]

export default function index() {
    const {isLandscape} = usePlatform()
    const [searchText, setSearchText] = React.useState('')
    const [sortConfig, setSortConfig] = React.useState({ key: 'dateOfBirth', direction: 'asc' });

    const [filters, setFilters] = React.useState([
        { label: 'Upcoming Days', inputType: 'dropdown', options: DAYS_OPTIONS, value: 30, defaultValue: 30 },
    ])

    const numberOfDays = filters.find(f => f.label === 'Upcoming Days')?.value || 30
    
    const filterState = useFilter('birthday-report');

    const { data, isLoading } = Query.reports.birthdays({
        days: numberOfDays, 
        filters: {values:filterState.peopleFilters, logic: filterState.filterLogic},
        searchText
    })

    const handleClearFilters = () => {
        setFilters([
            { label: 'Upcoming Days', inputType: 'dropdown', options: DAYS_OPTIONS, value: 30, defaultValue: 30 },
        ])
        setSearchText('')
    }
    
    const calculateNextBirthdayAge = (dateOfBirth) => {
        if (!dateOfBirth) return null;
        const birthDate = DateTime.fromISO(dateOfBirth);
        const currentYear = DateTime.now().year;
        
        let nextBirthday = birthDate.set({ year: currentYear });
        if (nextBirthday < DateTime.now().startOf('day')) {
            nextBirthday = nextBirthday.set({ year: currentYear + 1 });
        }
        
        return nextBirthday.year - birthDate.year;
    };
    
    const compareDates = (a, b, key, direction) => {
        let comparison = 0;
        
        if (key === 'dateOfBirth') {
            const dateA = DateTime.fromISO(a.stats.dateOfBirth).set({ year: DateTime.now().year });
            const dateB = DateTime.fromISO(b.stats.dateOfBirth).set({ year: DateTime.now().year });
            
            const now = DateTime.now();
            const adjustedA = dateA < now.startOf('day') ? dateA.plus({ years: 1 }) : dateA;
            const adjustedB = dateB < now.startOf('day') ? dateB.plus({ years: 1 }) : dateB;
            
            comparison = adjustedA < adjustedB ? -1 : 1;
        } else {
            const valA = a.stats[key] || '';
            const valB = b.stats[key] || '';
            
            if (typeof valA === 'string' && typeof valB === 'string') {
                comparison = valA.localeCompare(valB);
            } else {
                comparison = valA < valB ? -1 : 1;
            }
        }
        
        return direction === 'asc' ? comparison : -comparison;
    };

    const filteredData = useMemo(() => {
        if (!data) return []
        
        let processedData = data;
        
        if (searchText.trim()) {
            const search = searchText.toLowerCase().trim()
            processedData = processedData.filter(row => {
                const item = row.stats
                const fullName = `${item.firstName} ${item.lastName}`.toLowerCase()
                const email = (item.email || '').toLowerCase()
                const phone = (item.Phones || []).map(p => p.phoneNumber).join(' ').toLowerCase()
                
                return fullName.includes(search) || email.includes(search) || phone.includes(search)
            })
        }
        
        if (sortConfig) {
            processedData = [...processedData].sort((a, b) => compareDates(a, b, sortConfig.key, sortConfig.direction));
        }
        
        return processedData;
    }, [data, searchText, sortConfig])

    const columns = [
        {value: 'Name', width:isLandscape ? undefined : 250, sortable: true, accessor: 'firstName', renderer: (row) => {
            const item = row.stats;
            
            return <View style={styles.personHeadingContainer}>
                <Avatar size={30} profile={{id: item.id, avatarUpdatedAt: item.avatarUpdatedAt, firstName: item.firstName, lastName: item.lastName}} disableBorder/>
                <View style={{paddingLeft:10}}>
                    <Text style={styles.personHeading}>{item.firstName} {item.lastName}</Text>
                    <Text style={styles.personSubHeading}>{item.email}</Text>
                </View>
            </View>
        }},
        {value: 'Birthday', sortable: true, accessor: 'dateOfBirth', renderer: (row) => {
            const item = row.stats;
            return <Text style={styles.colText()}>{item.birthday}</Text>
        }},
        {value: 'Age', sortable: true, accessor: 'dateOfBirth', renderer: (row) => {
            const item = row.stats;
            const age = calculateNextBirthdayAge(item.dateOfBirth);
            return <Text style={styles.colText()}>{age}</Text>
        }},
        {value: 'Phone', renderer: (row) => {
            const item = row.stats;
            return (
                <View style={{gap:5}}>
                    {(item.Phones || []).map(p => <Pressable key={p.phoneNumber} onPress={() => openURL(`tel:${p.phoneNumber.replace(/ /g, '')}`)}><Text style={styles.personSubHeading}>{p.phoneNumber}</Text></Pressable>)}
                </View>
            );
        }},
        {value: 'Last Attended', sortable: true, accessor: 'lastVisit', renderer: (row) => {
            const item = row.stats;
            if (!item.lastVisit) return <Text style={styles.colText('textSecondary')}>Never</Text>
            return <Text style={styles.colText()}>{DateTime.fromISO(item.lastVisit).toLocaleString(DateTime.DATE_MED)}</Text>
        }},
        {value: 'Days Since Last Visit', sortable: true, accessor: 'lastVisit', renderer: (row) => {
            const item = row.stats;
            if (!item.lastVisit) return <Text style={styles.colText('textSecondary')}>N/A</Text>
            const days = Math.abs(Math.floor(DateTime.fromISO(item.lastVisit).diffNow('days').days))
            const weeks = Math.floor(days / 7)
            const remainingDays = days % 7

            const conditionalColor = days < 14 
                ? 'textSecondary'
                : days < 21
                    ? 'warning'
                    : 'error'
            return <Text style={styles.colText(conditionalColor)}>{weeks} weeks {remainingDays} days</Text>
        }}
    ]

    return (
        <View style={{flex:1, paddingTop:10}}>
            <Table 
                title="Upcoming Birthdays"
                columns={columns} 
                data={filteredData} 
                isLoading={isLoading}
                filters={filters}
                onFiltersChange={setFilters}
                onSearchChange={setSearchText}
                onClearFilters={handleClearFilters}
                idAccessor={(row) => row.stats?.id}
                sortConfig={sortConfig}
                onSortChange={setSortConfig}
                headerRightContent={<PeopleAdvancedFilter filterId="birthday-report" />}
            />
        </View>
    )
}

const styles = StyleSheet.create(({colors, fonts}) => ({
    personHeadingContainer: {
        flexDirection:"row", 
        alignItems:"center", 
        gap:5
    },
    personHeading: {
        fontSize:14, 
        fontFamily:fonts.heading, 
        color:colors.text
    },
    personSubHeading: {
        color:colors.primary,
        fontFamily:fonts.body,
        fontSize:12,
        fontStyle:"italic",
        _web:{
            cursor:"pointer",
            _hover:{
                color:colors.secondary,
            }
        }
    },
    colText: (color) => ({
        fontFamily:fonts.body,
        fontSize:14,
        color:colors[color] || colors.textSecondary
    })
}))